/**
 * Global Error Boundary + Runtime Error Reporter
 *
 * Catches React render errors and unhandled JS errors,
 * displays a recovery UI, and logs errors for diagnostics.
 */

import React, { Component, ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Copy, Check } from 'lucide-react';
import { getStoredToken } from '../lib/localAuth';
import { API_BASE } from '../lib/api';

interface ErrorState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

interface Props {
  children: React.ReactNode;
}

// In-memory error log for the diagnostics panel
const ERROR_LOG: { timestamp: string; message: string; stack?: string; source?: string }[] = [];
const MAX_ERRORS = 50;

/** Get the collected runtime errors */
export function getErrorLog() {
  return [...ERROR_LOG];
}

/** Clear the error log */
export function clearErrorLog() {
  ERROR_LOG.length = 0;
}

/** Log a runtime error */
function logError(message: string, stack?: string, source?: string) {
  ERROR_LOG.unshift({
    timestamp: new Date().toISOString(),
    message,
    stack: stack?.slice(0, 500),
    source,
  });
  if (ERROR_LOG.length > MAX_ERRORS) ERROR_LOG.pop();
  void reportErrorAsBug(message, stack, source);
}

async function reportErrorAsBug(message: string, stack?: string, source?: string) {
  try {
    const token = getStoredToken();
    if (!token) return;
    await fetch(`${API_BASE}/api/admin/bugs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        title: `Frontend error: ${String(message || 'Unknown').slice(0, 120)}`,
        description: `${message || ''}\n\n${stack || ''}`.slice(0, 4000),
        page: typeof window !== 'undefined' ? window.location.pathname : '',
        priority: 'high',
        source: 'frontend_error_boundary',
        metadata: {
          source,
          href: typeof window !== 'undefined' ? window.location.href : '',
        },
      }),
    });
  } catch {
    // Never throw from error reporter.
  }
}

// Register global error handlers (called once on module load)
if (typeof window !== 'undefined') {
  // Catch unhandled JS errors
  window.addEventListener('error', (event) => {
    logError(
      event.message || 'Unknown error',
      event.error?.stack,
      `${event.filename}:${event.lineno}:${event.colno}`
    );
  });

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const message = event.reason?.message || String(event.reason) || 'Unhandled promise rejection';
    logError(message, event.reason?.stack, 'Promise');
  });

  // Intercept console.error to capture React warnings
  const originalError = console.error;
  console.error = (...args: any[]) => {
    const message = args.map(a => (typeof a === 'string' ? a : String(a))).join(' ');
    // Only log actual errors, not React dev warnings
    if (!message.includes('Warning:') && !message.includes('act(')) {
      logError(message.slice(0, 300), undefined, 'console.error');
    }
    originalError.apply(console, args);
  };
}

class ErrorBoundary extends Component<Props, ErrorState> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    logError(
      error.message,
      error.stack,
      `React: ${errorInfo.componentStack?.slice(0, 200)}`
    );
  }

  handleReload = () => {
    window.location.reload();
  };

  handleRecover = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback
        error={this.state.error}
        onReload={this.handleReload}
        onRecover={this.handleRecover}
      />;
    }
    return this.props.children;
  }
}

/** Fallback UI shown when a render error occurs */
const ErrorFallback: React.FC<{
  error: Error | null;
  onReload: () => void;
  onRecover: () => void;
}> = ({ error, onReload, onRecover }) => {
  const [copied, setCopied] = React.useState(false);

  const copyError = () => {
    const text = `Error: ${error?.message}\n\nStack:\n${error?.stack || 'N/A'}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-lg w-full space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-destructive" />
          </div>
          <div>
            <h1 className="font-display text-xl font-bold text-foreground">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">An unexpected error occurred in the application.</p>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border bg-card p-4">
            <p className="text-sm font-mono text-destructive break-all">{error.message}</p>
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={onRecover}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Try to recover
          </button>
          <button
            onClick={onReload}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium hover:bg-muted transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Reload
          </button>
          <button
            onClick={copyError}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium hover:bg-muted transition-colors"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundary;
